import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  HomeIcon, 
  ClipboardDocumentListIcon, 
  BriefcaseIcon, 
  AdjustmentsHorizontalIcon, 
  ArrowRightOnRectangleIcon, 
  Bars3Icon, 
  XMarkIcon,
  ChartBarIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user } = useAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Update the logoGlow based on scroll position
  const logoGlow = {
    filter: isScrolled || user 
      ? 'drop-shadow(0 0 12px rgba(59, 130, 246, 0.5))'
      : 'drop-shadow(0 0 12px rgba(255, 255, 255, 0.5))'
  };

  const navigationItems = [
    {
      to: "/jobs",
      icon: <BriefcaseIcon className="w-5 h-5" />,
      label: "Discover Opportunities",
    },
    {
      to: "/board",
      icon: <ClipboardDocumentListIcon className="w-5 h-5" />,
      label: "Track Progress",
    },
    {
      to: "/reporting",
      icon: <ChartBarIcon className="w-5 h-5" />,
      label: "Smart Insights",
    },
    {
      to: "/pricing",
      icon: <SparklesIcon className="w-5 h-5" />,
      label: "Plans",
    }
  ];

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled || user
          ? 'bg-white/90 backdrop-blur-xl shadow-lg' 
          : 'bg-gradient-to-b from-black/20 to-transparent backdrop-blur-sm'
      }`}
    >
      <nav className="container mx-auto px-4 sm:px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo with enhanced visibility */}
          <Link 
            to={user ? "/jobs" : "/"} 
            className="flex items-center space-x-3 group relative"
          >
            {/* Brand logo. The gradient artwork reads well on the white header;
                over the dark hero it is swapped for the white cut-out. */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
              style={logoGlow}
            >
              <img
                src={isScrolled || user ? '/logo.png' : '/logo-white.png'}
                alt="JOE AI"
                className="h-11 w-auto"
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {user && (
              <motion.div 
                className="flex items-center space-x-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {navigationItems.map((item) => (
                  <Link 
                    key={item.to}
                    to={item.to} 
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl transition-all duration-300 font-medium ${
                      isActive(item.to)
                        ? 'text-blue-600 bg-blue-50/80 shadow-sm' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                ))}
              </motion.div>
            )}

            

            <div className="flex items-center space-x-4">
              {user && process.env.NODE_ENV === 'development' && (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link 
                    to="/search" 
                    className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-xl font-medium"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <AdjustmentsHorizontalIcon className="w-5 h-5" />
                    <span>Advanced Search</span>
                  </Link>
                </motion.div>
              )}
              
              {user ? (
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <button 
                    onClick={() => {
                      localStorage.removeItem('token');
                      window.location.href = '/login';
                    }}
                    className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm border-2 border-red-200/20 text-red-500 px-6 py-2.5 rounded-xl hover:bg-red-50/90 transition-all font-medium"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <ArrowRightOnRectangleIcon className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </motion.div>
              ) : (
                <div className="flex items-center space-x-6">
                  {!isActive('/login') && !isActive('/register') && (
                    <>
                      <Link
                        to="/pricing"
                        className={`font-medium transition-colors ${
                          isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Pricing
                      </Link>
                      <Link 
                        to="/login"
                        className={`font-medium transition-colors ${
                          isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white hover:text-blue-200'
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Login
                      </Link>
                      <Link 
                        to="/register"
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-xl font-medium"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Get Started
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 rounded-xl transition-colors ${
              isScrolled ? 'hover:bg-gray-100 text-gray-700' : 'hover:bg-white/10 text-white'
            }`}
          >
            {isMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </motion.button>
        </div>

        {/* Mobile Navigation with improved styling */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden mt-4"
            >
              <div className="flex flex-col space-y-2 pt-4 border-t border-gray-100/10 bg-white/80 backdrop-blur-xl rounded-2xl p-4 shadow-lg">
                {user && (
                  <>
                    <div className="flex items-center space-x-3 px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          Welcome back, {user.name}!
                        </p>
                        <p className="text-sm text-gray-500">
                          Your next opportunity awaits
                        </p>
                      </div>
                    </div>
                    
                    {navigationItems.map((item) => (
                      <Link 
                        key={item.to}
                        to={item.to} 
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center space-x-2 px-4 py-3 rounded-xl transition-all font-medium ${
                          isActive(item.to)
                            ? 'text-blue-600 bg-blue-50 shadow-sm'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    ))}
                    
                    {process.env.NODE_ENV === 'development' && (
                      <Link 
                        to="/search" 
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl"
                      >
                        <AdjustmentsHorizontalIcon className="w-5 h-5" />
                        <span>Advanced Search</span>
                      </Link>
                    )}
                    
                    <button 
                      onClick={() => {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                      }}
                      className="flex items-center space-x-2 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <ArrowRightOnRectangleIcon className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};

export default Header;